import { createSlice } from "@reduxjs/toolkit";

export const MODULES = {
  MAKERSLAB: "makerslab",
  COWORK: "cowork",
  EDUSPACE: "eduspace",
  BLOOMBERG: "bmb",
};

const STORAGE_KEY = "selectedModule";

// Map assorted URL keywords → a module. Accepts the values used by the deployed
// subdomains as well as friendlier aliases for the ?module= override.
const URL_ALIASES = [
  { module: MODULES.MAKERSLAB, keys: ["makerslab", "makers", "makersv2", "lab"] },
  { module: MODULES.EDUSPACE, keys: ["eduspace", "classroom", "class"] },
  { module: MODULES.BLOOMBERG, keys: ["bloomberg", "bmb"] },
  { module: MODULES.COWORK, keys: ["cowork"] },
];

const matchAlias = (value) => {
  const v = (value || "").toLowerCase();
  if (!v) return null;
  // Exact module value (e.g. ?module=cowork) first, then keyword contains.
  if (Object.values(MODULES).includes(v)) return v;
  const hit = URL_ALIASES.find((a) => a.keys.some((k) => v.includes(k)));
  return hit ? hit.module : null;
};

// Resolve the module the URL is asking for: an explicit ?module= query param
// wins (handy for deep links / local testing), else the hostname (each module
// has its own subdomain in production). Returns null when the URL is neutral.
export const getModuleFromUrl = () => {
  if (typeof window === "undefined") return null;
  try {
    const param = new URLSearchParams(window.location.search).get("module");
    const fromParam = matchAlias(param);
    if (fromParam) return fromParam;
  } catch (e) {
    // URLSearchParams unavailable — fall through to hostname.
  }
  return matchAlias(window.location?.hostname || "");
};

// Precedence: URL (query param → hostname) → localStorage → CoWork default.
// The URL wins at load so opening a module's URL always starts on that module;
// on a neutral domain (no URL hint) the user's last choice persists.
const getInitialModule = () => {
  const fromUrl = getModuleFromUrl();
  if (fromUrl) return fromUrl;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && Object.values(MODULES).includes(stored)) return stored;

  return MODULES.COWORK;
};

const appModuleSlice = createSlice({
  name: "appModule",
  initialState: {
    selectedModule: getInitialModule(),
  },
  reducers: {
    setSelectedModule: (state, action) => {
      if (!Object.values(MODULES).includes(action.payload)) return;
      state.selectedModule = action.payload;
      localStorage.setItem(STORAGE_KEY, action.payload);
    },
  },
});

export const { setSelectedModule } = appModuleSlice.actions;

// Roles are namespaced: "Cowork Lab *" → Makers Lab, "Cowork Class *" →
// Eduspace, "BMB *" → Bloomberg, and any other "Cowork *" role → the base CoWork
// module. Returns the accessible modules in preferred display order (CoWork
// first), so callers can decide whether to show a switcher (>1) or lock to a
// single module.
export const getModulesForRoles = (roles = []) => {
  const list = Array.isArray(roles) ? roles : [];
  const isString = (r) => typeof r === "string";
  const hasLab = list.some((r) => isString(r) && r.includes("Lab"));
  const hasClass = list.some((r) => isString(r) && r.includes("Class"));
  const hasBmb = list.some((r) => isString(r) && r.includes("BMB"));
  const hasCowork = list.some(
    (r) => isString(r) && r.startsWith("Cowork") && !r.includes("Lab") && !r.includes("Class")
  );

  const modules = [];
  if (hasCowork) modules.push(MODULES.COWORK);
  if (hasLab) modules.push(MODULES.MAKERSLAB);
  if (hasClass) modules.push(MODULES.EDUSPACE);
  if (hasBmb) modules.push(MODULES.BLOOMBERG);
  return modules;
};

export const selectAppModule = (state) => state.appModule.selectedModule;
export const selectIsMakersLab = (state) =>
  state.appModule.selectedModule === MODULES.MAKERSLAB;
export const selectIsCowork = (state) =>
  state.appModule.selectedModule === MODULES.COWORK;
export const selectIsEduspace = (state) =>
  state.appModule.selectedModule === MODULES.EDUSPACE;
export const selectIsBloomberg = (state) =>
  state.appModule.selectedModule === MODULES.BLOOMBERG;

export default appModuleSlice;
